import { useCallback } from 'react';
import { Modal, Stack, Text } from '@adc/parallax-component-library';

import { type ConfigState } from '~/reducers/config/types';
import i18n from '~/utilities/i18n';

type ValidationType = Pick<ConfigState, 'validation'>;

const translationsKeysData = {
  createAccount: {
    differentPasswordTitleKey:
      'CreateAccountAccountInfo.modals.accountInfoErrors.differentPasswordTitle',
    differentPasswordSubtitleKey: '',
  },
  updatePassword: {
    differentPasswordTitleKey:
      'ChangePassword.modals.changePasswordErrors.reusedPasswordError.title',
    differentPasswordSubtitleKey:
      'ChangePassword.modals.changePasswordErrors.reusedPasswordError.message',
  },
} as const;

const useAccount = (type?: 'createAccount' | 'updatePassword') => {
  const translationKeys = translationsKeysData[type ?? 'createAccount'];

  const showPasswordValidationError = useCallback(
    (
      { validation }: ValidationType,
      errors?: { error: string; key: string }[],
    ) => {
      Modal.show({
        testID:
          'CreateAccountAccountInfo.modals.accountInfoErrors.securityRules',
        header: '',
        primaryAction: {
          label: i18n.t('Global.microcopy.common.okCap'),
        },
        details: (
          <Stack gap="$4">
            <Text>{i18n.t(translationKeys.differentPasswordTitleKey)}</Text>

            {translationKeys.differentPasswordSubtitleKey !== '' ? (
              <Text>
                {i18n.t(translationKeys.differentPasswordSubtitleKey)}
              </Text>
            ) : null}

            {errors?.map((e) => {
              const limit: number | undefined =
                e.error === 'max'
                  ? Number(
                      validation?.identity.find(
                        (item) => item.data.key === e.error,
                      )?.data.value,
                    )
                  : Number(
                      validation?.identity.find((item) => item.type === e.error)
                        ?.data.limit,
                    );

              return (
                <Text key={e.error}>
                  {i18n.t(
                    `CreateAccountAccountInfo.modals.accountInfoErrors.securityRules.${e.error as 'context'}`,
                    {
                      count: Number.isNaN(limit) ? 0 : limit,
                    },
                  )}
                </Text>
              );
            })}
          </Stack>
        ),
      });
    },
    [
      translationKeys.differentPasswordSubtitleKey,
      translationKeys.differentPasswordTitleKey,
    ],
  );

  return { showPasswordValidationError };
};

export default useAccount;
